import { useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { Settings2 } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

const STORAGE_KEY = 'kestrel-streaming-params-3'

interface StreamingParams {
  maxBitrate: string
  minBitrate: string
  maxFps: string
  disableMotionBlur: boolean
  minQuality: string
}

export const DEFAULT_PARAMS: StreamingParams = {
  maxBitrate: '100000',
  minBitrate: '3000',
  maxFps: '45',
  disableMotionBlur: false,
  minQuality: '1',
}

// Check if any param differs from default
const paramsDifferFromDefaults = (params: StreamingParams): boolean => {
  return (
    params.maxBitrate !== DEFAULT_PARAMS.maxBitrate ||
    params.minBitrate !== DEFAULT_PARAMS.minBitrate ||
    params.maxFps !== DEFAULT_PARAMS.maxFps ||
    params.disableMotionBlur !== DEFAULT_PARAMS.disableMotionBlur ||
    params.minQuality !== DEFAULT_PARAMS.minQuality
  )
}

// Load params from localStorage, return null if not set
export const loadStoredStreamingParams = (): StreamingParams | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Only return if it has all required fields
      if (parsed.maxBitrate && parsed.minBitrate && parsed.maxFps) {
        return {
          maxBitrate: parsed.maxBitrate,
          minBitrate: parsed.minBitrate,
          maxFps: parsed.maxFps,
          disableMotionBlur:
            parsed.disableMotionBlur ?? DEFAULT_PARAMS.disableMotionBlur,
          minQuality: parsed.minQuality ?? DEFAULT_PARAMS.minQuality,
        }
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

const PanelContainer = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 8rem;
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  padding: 0.75rem;
  z-index: 2000;
  backdrop-filter: blur(4px);
  min-width: 200px;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05rem;

  svg {
    width: 1rem;
    height: 1rem;
    opacity: 0.8;
  }
`

const InputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  gap: 0.5rem;

  &:last-of-type {
    margin-bottom: 0.75rem;
  }
`

const Label = styled.label`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const DefaultHint = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.65rem;
  font-weight: normal;
`

const Input = styled.input`
  width: 80px;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: white;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
  text-align: right;

  &:focus {
    outline: none;
    border-color: rgba(24, 144, 255, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #1890ff;
`

const CheckboxLabel = styled.label`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`

const ApplyButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  background: #1890ff;
  border: none;
  border-radius: 0.25rem;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #40a9ff;
  }

  &:active {
    transform: scale(0.98);
  }
`

const ToggleButton = styled.button<{ $hasCustomSettings: boolean }>`
  position: absolute;
  bottom: 0.5rem;
  right: 8rem;
  width: 3rem;
  height: 3rem;
  background: transparent;
  border: none;
  color: ${(props) => (props.$hasCustomSettings ? '#40a9ff' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 500;
  pointer-events: auto;

  &:hover {
    color: ${(props) =>
      props.$hasCustomSettings ? '#1890ff' : 'rgba(255, 255, 255, 0.8)'};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.9);
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8));
  }
`

const loadStoredParams = (): StreamingParams => {
  const stored = loadStoredStreamingParams()
  return stored ?? DEFAULT_PARAMS
}

const saveParams = (params: StreamingParams) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params))
  } catch {
    // Ignore storage errors
  }
}

export const StreamingParamsPanel: React.FC = () => {
  const { sendCommand } = useKestrelViewerContext()
  const [isOpen, setIsOpen] = useState(false)
  const [params, setParams] = useState<StreamingParams>(loadStoredParams)
  const hasCustomSettings = paramsDifferFromDefaults(params)

  // Persist to localStorage whenever params change
  useEffect(() => {
    saveParams(params)
  }, [params])

  const handleInputChange = useCallback(
    (field: keyof StreamingParams) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setParams((prev) => ({ ...prev, [field]: e.target.value }))
      },
    []
  )

  const handleCheckboxChange = useCallback(
    (field: keyof StreamingParams) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setParams((prev) => ({ ...prev, [field]: e.target.checked }))
      },
    []
  )

  const handleApply = useCallback(() => {
    // UI displays kbps, but we send bps over the wire
    const maxBitrateKbps = parseInt(params.maxBitrate, 10) || 0
    const minBitrateKbps = parseInt(params.minBitrate, 10) || 0
    const minQuality = Math.min(
      100,
      Math.max(1, parseInt(params.minQuality, 10) || 1)
    )

    const command = {
      command: KestrelCommands.SET_STREAMING_PARAMS,
      maxBitrate: maxBitrateKbps * 1000, // Convert kbps to bps
      minBitrate: minBitrateKbps * 1000, // Convert kbps to bps
      maxFps: parseInt(params.maxFps, 10) || DEFAULT_PARAMS.maxFps,
      disableMotionBlur: params.disableMotionBlur,
      minQuality,
    }
    sendCommand(command)
  }, [params, sendCommand])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent keyboard events from reaching pixelstreaming
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()

    if (e.key === 'Enter') {
      handleApply()
    }
  }

  if (!isOpen) {
    return (
      <ToggleButton
        onClick={() => setIsOpen(true)}
        title={
          hasCustomSettings
            ? 'Streaming Parameters (Custom)'
            : 'Streaming Parameters'
        }
        $hasCustomSettings={hasCustomSettings}>
        <Settings2 />
      </ToggleButton>
    )
  }

  return (
    <PanelContainer onKeyDown={handleKeyDown}>
      <PanelHeader>
        <Settings2 />
        Streaming Params
        <button
          onClick={() => setIsOpen(false)}
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '1rem',
            padding: 0,
          }}>
          ×
        </button>
      </PanelHeader>

      <InputRow>
        <Label>
          Max Bitrate (kbps)
          <DefaultHint>default: {DEFAULT_PARAMS.maxBitrate}</DefaultHint>
        </Label>
        <Input
          type='text'
          value={params.maxBitrate}
          onChange={handleInputChange('maxBitrate')}
          placeholder={DEFAULT_PARAMS.maxBitrate}
        />
      </InputRow>

      <InputRow>
        <Label>
          Min Bitrate (kbps)
          <DefaultHint>default: {DEFAULT_PARAMS.minBitrate}</DefaultHint>
        </Label>
        <Input
          type='text'
          value={params.minBitrate}
          onChange={handleInputChange('minBitrate')}
          placeholder={DEFAULT_PARAMS.minBitrate}
        />
      </InputRow>

      <InputRow>
        <Label>
          Max FPS
          <DefaultHint>default: {DEFAULT_PARAMS.maxFps}</DefaultHint>
        </Label>
        <Input
          type='text'
          value={params.maxFps}
          onChange={handleInputChange('maxFps')}
          placeholder={DEFAULT_PARAMS.maxFps}
        />
      </InputRow>

      <InputRow>
        <Label>
          Min Quality (1-100)
          <DefaultHint>default: {DEFAULT_PARAMS.minQuality}</DefaultHint>
        </Label>
        <Input
          type='number'
          min='1'
          max='100'
          value={params.minQuality}
          onChange={handleInputChange('minQuality')}
          placeholder={DEFAULT_PARAMS.minQuality}
        />
      </InputRow>

      <InputRow>
        <CheckboxLabel>
          <Checkbox
            type='checkbox'
            checked={params.disableMotionBlur}
            onChange={handleCheckboxChange('disableMotionBlur')}
          />
          Disable Motion Blur
        </CheckboxLabel>
      </InputRow>

      <ApplyButton onClick={handleApply}>Apply</ApplyButton>
    </PanelContainer>
  )
}
